import React, { useCallback, useEffect, useMemo } from "react";
import { PromoInfoShort, PromoStatusEnum } from "../../../mvcapi/models/common";
import { PromoCodeInputControl } from "./promo-code-input-control";
import { PromoDesactivationControl } from "./promo-desactivation-control";
import { PromoMessageImportantControl } from "./promo-message-important-control";
import TranslateManager from 'ts-translate-js';
import MarkDown from 'react-markdown';
import { isEmpty } from "lodash";
import './promo-deactivation-control.scss';
import { useIFrameResize } from "../../../logic/hooks/utils";
import { AddToCartRequestBase } from "../../../mvcapi/models/buyticket/ticketselection";
import { PriceChangeSuggestion } from "../../../mvcapi/models/buyticket";

const tr = TranslateManager.boundTR;

type PromoProposalControlProps = {
    promoInfo: PromoInfoShort;
    hiddenPromo: boolean;
    showProposalMessage: boolean;
    onPromoUpdated: () => void;
    onContentChange?: () => void;
    addToCartRequest?: AddToCartRequestBase; // Optional. Used for price change suggestion.
    onPriceChangeSuggestions?: (priceChangeSuggestions: PriceChangeSuggestion[]) => void; // Optional. Used for price change suggestion.
    isAPackageSale: boolean;
};

export function PromoProposalControl({promoInfo, hiddenPromo, onPriceChangeSuggestions, showProposalMessage, addToCartRequest, onPromoUpdated, onContentChange, isAPackageSale}: PromoProposalControlProps){
    const [ showPromoCodeInputControl, setShowPromoCodeInputControl ] = React.useState(!showProposalMessage);
    const [ setInputFocus, setSetInputFocus ] = React.useState(false);

    useIFrameResize();

    useEffect(() => {
        if(onContentChange !== undefined) 
            onContentChange();
    }, [onContentChange]);
    
    const handlePromoSuggestionClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowPromoCodeInputControl(true);
        setSetInputFocus(true);
    }, [setShowPromoCodeInputControl, setSetInputFocus]);

    const handleOnPromoUpdated = useCallback(() => {
        if(onPromoUpdated) 
            onPromoUpdated();
    }, [onPromoUpdated]);

    const showPasswordInputCtl = promoInfo.PromoStatus === PromoStatusEnum.PromoAvailable
        || promoInfo.PromoStatus === PromoStatusEnum.PasswordNeeded;

    const promoCodeInputControlTitle = showProposalMessage 
        ? tr("Veuillez entrer le code de promotion") : tr("Vous avez un code promo?");

    const content = useMemo(() => {
        if(showPasswordInputCtl) {
            if(showPromoCodeInputControl) {
                return <PromoCodeInputControl 
                    promoKind={promoInfo.PromoKind}
                    promoID={promoInfo.PromoID} 
                    promoDefGroupingIDs={promoInfo.PromoDefGroupingIDs}
                    title={promoCodeInputControlTitle}
                    setInputFocus={setInputFocus}
                    onPromoUpdated={handleOnPromoUpdated}
                    onContentChange={onContentChange}
                    addToCartRequest={addToCartRequest}
                    onPriceChangeSuggestions={onPriceChangeSuggestions}
                    isAPackageSale={isAPackageSale}
                />;
            }
            else {
                //TODO: Refactor this as a button
                // eslint-disable-next-line sonarjs/anchor-is-valid
                return <a href="#" className="btn btn-default" onClick={handlePromoSuggestionClick}>
                    <MarkDown className="markdown-inline" source={!isEmpty(promoInfo.PromoSuggestionLinkText) 
                        ? promoInfo.PromoSuggestionLinkText 
                        : tr("Si vous détenez un **CODE DE PROMOTION**, cliquez ici.")} />
                </a>;
            }
        }
        else if(promoInfo.PromoStatus === PromoStatusEnum.InPromo){
            return <div className="promo-deactivation-control">
                <PromoDesactivationControl 
                    hiddenPromo={hiddenPromo} 
                    onPromoDeactivated={handleOnPromoUpdated}
                    addToCartRequest={addToCartRequest} 
                    onPriceChangeSuggestions={onPriceChangeSuggestions}
                    isAPackageSale={isAPackageSale}
                />
                <PromoMessageImportantControl message={promoInfo.PromoImportantInfo}/>
            </div>;
        }
    }, [showPasswordInputCtl, promoInfo.PromoStatus, promoInfo.PromoKind, promoInfo.PromoID, promoInfo.PromoDefGroupingIDs, promoInfo.PromoSuggestionLinkText, promoInfo.PromoImportantInfo, showPromoCodeInputControl, promoCodeInputControlTitle, setInputFocus, handleOnPromoUpdated, onContentChange, addToCartRequest, onPriceChangeSuggestions, isAPackageSale, handlePromoSuggestionClick, hiddenPromo]);

    return <div>{promoInfo && content}</div>;
}